import { Loader2, RefreshCw, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Button,
  Drawer,
  Space,
  Modal,
  Text,
  Container,
  Group,
} from "@mantine/core";
import {
  SearchInput,
  Button as JemButton,
} from "@jem-open/jem-ui";
import PayslipUploadHistoryTable from "../components/PayslipUploadHistoryTable";
import PageHeading from "../../../themes/components/PageHeading";
import PermissionProtectedComponent from "../../../common/uac/PermissionProtectedComponent";
import { PERMISSION_ADD_PAYSLIP } from "../../../utils/UserAccessControlUtils";
import PageContainer from "../../../themes/components/PageContainer";
import PayslipUploadForm from "../components/PayslipUploadForm";
import DocumentProcessingJob from "../services/model/DocumentProcessingJob";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import { payslipJourneyConfiguration } from "../payslipJourneyConfiguration";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import FeatureFlagProtectedComponent from "../../../common/feature-flagging/FeatureFlagProtectedComponent";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import { HELP_CENTER_URL } from "../../help-center/constants";
import useCrossBatchSearchApiService from "../services/api/CrossBatchSearchApiService";

const PayslipDirectoryPage: React.FC = () => {
  const documentImporterConfiguration: DocumentImporterConfiguration =
    payslipJourneyConfiguration;

  const [opened, setOpened] = useState(false);
  const [selectionDocumentUpload, setSelectionUpload] = useState<
    DocumentProcessingJob[]
  >([]);
  const [exitingUpload, setExitingUpload] = useState(false);
  const [uploadingClicked, setUploadingClicked] = useState(false);

  // Cross-batch search state
  const [searchTerm, setSearchTerm] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");

  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);

  const { isLoading: isSearchLoading, data: searchResults } =
    useCrossBatchSearchApiService({
      search: submittedSearch,
      document_type: documentImporterConfiguration.document_type,
    });

  // Build synthetic batch objects from cross-batch search results, grouped by job_id
  const searchBatches = React.useMemo(() => {
    if (!submittedSearch || !searchResults || searchResults.length === 0)
      return undefined;

    const jobMap = new Map<number, (typeof searchResults)[0]>();
    searchResults.forEach((r) => {
      if (!jobMap.has(r.job_id)) jobMap.set(r.job_id, r);
    });

    return Array.from(jobMap.values()).map((r) => ({
      id: r.job_id,
      batch_name: r.batch_name,
      uploaded_by: null,
      file_to_process: "",
      status: r.job_status,
      status_reason: "",
      documents_total_count: r.documents_total_count,
      documents_indexed_count: 0,
      documents_matched_count: r.documents_matched_count,
      documents_failed_count: r.documents_failed_count,
      date_created: "",
      date_issued: r.date_issued,
      date_of_distribution: r.date_of_distribution,
    }));
  }, [submittedSearch, searchResults]);

  const matchingJobIds =
    submittedSearch && searchResults
      ? new Set(searchResults.map((r) => r.job_id))
      : undefined;

  const debouncedTrackSearchEvent = useCallback(
    debounce((value: string) => {
      if (value.length === 0) return;
      trackDocumentsEvent(
        {
          event: "Cross_Batch_Search_Input",
          description: `User types in cross-batch search on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
          event_type: "Search_Bar_Input",
          job: null,
          type: documentImporterConfiguration.document_name_plural,
        },
        { search_input: value }
      );
    }, 500),
    [trackDocumentsEvent, documentImporterConfiguration]
  );

  useEffect(
    () => () => {
      debouncedTrackSearchEvent.cancel();
    },
    [debouncedTrackSearchEvent]
  );

  const submitSearch = (): void => {
    setSubmittedSearch(searchTerm.trim());
  };

  const handleClearSearch = (): void => {
    debouncedTrackSearchEvent.cancel();
    setSearchTerm("");
    setSubmittedSearch("");
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter") {
      submitSearch();
    }
  };

  useEffect(() => {
    trackDocumentsEvent({
      event: `${documentImporterConfiguration.document_name_plural}_Dashboard`,
      description: `User navigates to the ${documentImporterConfiguration.document_name_plural} dashboard`,
      event_type: "PageView",
      job: null,
      type: documentImporterConfiguration.document_name_plural,
    });
  }, [trackDocumentsEvent]);

  if (!hasPermissions) {
    return <NotFound />;
  }

  return (
    <PageContainer>
      <Drawer
        opened={opened}
        size="xl"
        onClose={() => {
          if (uploadingClicked) {
            setExitingUpload(true);
          } else {
            setOpened(false);
          }
        }}
        title={`Upload ${documentImporterConfiguration.document_name_plural}`}
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <PayslipUploadForm
          documentImporterConfiguration={documentImporterConfiguration}
          onSuccess={() => {
            setOpened(false);
            setExitingUpload(false);
            setUploadingClicked(false);
          }}
          setExitingUpload={setExitingUpload}
          setUploadingClicked={setUploadingClicked}
        />
      </Drawer>
      <Modal
        size="40%"
        title={`Do you want to cancel this ${documentImporterConfiguration.document_name_lower_case} upload?`}
        opened={exitingUpload}
        centered
        onClose={() => setExitingUpload(false)}
        closeOnClickOutside
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Container>
          <Space h="xl" />
          <Text size="lg" align="center">
            Closing this window will cancel your current payslip upload
          </Text>
          <Space h="xl" />
          <Group spacing="xl" position="center">
            <Button
              color="red"
              style={{ backgroundColor: "red" }}
              onClick={() => {
                setOpened(false);
                setExitingUpload(false);
              }}
            >
              Cancel Upload
            </Button>
            <Button
              onClick={() => {
                setOpened(true);
                setExitingUpload(false);
              }}
            >
              Continue Upload
            </Button>
          </Group>
          <Space h="xl" />
        </Container>
      </Modal>

      <PageHeading
        title={`${documentImporterConfiguration.document_name_plural}`}
      >
        <PermissionProtectedComponent
          requiredViewPermissions={[PERMISSION_ADD_PAYSLIP]}
        >
          <ActionIcon
            id="payslips-refresh-btn"
            variant="outline"
            className="inline-flex"
            aria-label="Refresh"
            sx={{
              border: "1px solid var(--grey-grey-50)",
            }}
            size={34}
            onClick={() => {
              trackDocumentsEvent({
                event: "Refresh_Icon",
                description: `User clicked 'refresh' icon on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
                event_type: "CTA:Click",
                job: null,
                type: documentImporterConfiguration.document_name_plural,
              });
              window.location.reload();
            }}
          >
            <RefreshCw size={18} />
          </ActionIcon>

          <Button
            id="payslips-upload-btn"
            type="button"
            variant="filled"
            onClick={() => {
              trackDocumentsEvent({
                event: `Upload_${documentImporterConfiguration.document_name_plural}_CTA_Header`,
                description: `User clicked 'Upload ${documentImporterConfiguration.document_name_plural}' icon on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard header`,
                event_type: "CTA:Click",
                job: null,
                type: documentImporterConfiguration.document_name_plural,
              });
              setOpened(true);
            }}
            className="inline-flex"
            color="jem-theme"
          >
            Upload {documentImporterConfiguration.document_name_plural}
          </Button>
          <FeatureFlagProtectedComponent featureName={FeatureFlags.HELP_CENTER}>
            <Button
              id="help-center-contact-us-btn"
              type="button"
              variant="outline"
              p="0 20px"
              onClick={() => {
                trackDocumentsEvent({
                  event: `Need_Assistance_CTA`,
                  description: `User clicked 'Need Assistance' button on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
                  event_type: "CTA:Click",
                  job: null,
                  type: documentImporterConfiguration.document_name_plural,
                });
                window.open(HELP_CENTER_URL, "_blank");
              }}
              sx={{
                "&:active": {
                  backgroundColor: "rgba(237, 119, 135, 1)",
                },
              }}
              className="inline-flex secondary-action"
              color="grey"
            >
              Need Assistance?
            </Button>
          </FeatureFlagProtectedComponent>
        </PermissionProtectedComponent>
      </PageHeading>

      <div className="flex items-center gap-xs mt-md mb-md max-w-md">
        <SearchInput
          id="payslips-cross-batch-search"
          placeholder="Search across all batches"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            debouncedTrackSearchEvent(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onClear={handleClearSearch}
        />
        <Button
          id="payslips-cross-batch-search-btn"
          variant="filled"
          color="jem-theme"
          aria-label={isSearchLoading ? "Searching" : "Search"}
          aria-busy={isSearchLoading}
          onClick={submitSearch}
          disabled={isSearchLoading}
          compact
          sx={{
            width: 34,
            height: 34,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSearchLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Search size={18} />
          )}
        </Button>
      </div>

      {submittedSearch && (
        <Breadcrumbs separator=">" mb="sm">
          <Anchor
            href="/payslips"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              handleClearSearch();
            }}
            style={{ color: "rgba(6, 33, 51, 0.5)" }}
          >
            {documentImporterConfiguration.document_name_plural}
          </Anchor>
          <span style={{ color: "#062133" }}>Search Results</span>
        </Breadcrumbs>
      )}

      {submittedSearch && matchingJobIds && matchingJobIds.size > 0 && (
        <p className="text-sm text-greyscale-text-caption mb-sm">
          {matchingJobIds.size} batch{matchingJobIds.size !== 1 ? "es" : ""}{" "}
          found matching &quot;{submittedSearch}&quot;
        </p>
      )}

      {submittedSearch && isSearchLoading && (
        <p className="text-sm text-greyscale-text-caption mb-sm">
          Searching across all batches...
        </p>
      )}

      {submittedSearch && !isSearchLoading && matchingJobIds && matchingJobIds.size === 0 ? (
        <div className="text-center py-xl">
          <p className="text-lg font-medium mb-xs text-greyscale-text-title">
            No batches found
          </p>
          <p className="text-sm text-greyscale-text-caption mb-md">
            No unmatched{" "}
            {documentImporterConfiguration.document_name_plural_lower_case}{" "}
            found for &quot;{submittedSearch}&quot;
          </p>
          <JemButton variant="outline" onClick={handleClearSearch}>
            Clear Search
          </JemButton>
        </div>
      ) : (
        !isSearchLoading && (
          <PayslipUploadHistoryTable
            documentImporterConfiguration={documentImporterConfiguration}
            onSelection={setSelectionUpload}
            selection={selectionDocumentUpload}
            matchingJobIds={matchingJobIds}
            searchBatches={searchBatches}
          />
        )
      )}
    </PageContainer>
  );
};

export default PayslipDirectoryPage;
